import type {
  BrandSpecials,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import type {
  BrandSpecials as SDKBrandSpecials,
  Specials,
} from '@iheartjane/dm-sdk';
import { Placement, SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import { Box, Button, Flex, Typography } from '@iheartjane/reefer';
import type { ComplianceLanguageSettings, Nullable } from '@iheartjane/types';
import kebabCase from 'lodash-es/kebabCase';
import { useMemo } from 'react';

import { DMMenuRow } from '@jane/bloom/components/menuRow/dmMenuRow';
import { JaneGoldRow } from '@jane/bloom/components/menuRow/janeGoldRow';
import { MenuRow } from '@jane/bloom/components/menuRow/menuRow';
import { SpecialsRow } from '@jane/bloom/components/menuRow/specialsRow';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import { isJaneUser } from '@jane/bloom/types/schemas/marketplace/janeUser';
import type { StoreSpecial } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import { sortSpecials } from '@jane/bloom/utils/specials/sortSpecials';
import { formatProductCount } from '@jane/bloom/utils/strings/formatProductCount';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';

import styles from './featuredTab.module.css';

export type FeaturedTabProps = {
  brandSpecials: BrandSpecials;
  complianceLanguage?: Nullable<ComplianceLanguageSettings>;
  customLanguage: CustomLanguage;
  enabledSpecialsForRow: StoreSpecial[];
  featuredPlacements: (string | Specials | SDKBrandSpecials)[];
  janeGold: JaneGold;
  menuTopRowInstance?: SmartSort<MaterializedMenuProduct>;
  pauseRefetching: () => void;
  shopAllProductCount?: number;
  unpauseRefetching: () => void;
  userHasLinkedBank?: boolean;
};

export const FeaturedTab = ({
  brandSpecials,
  complianceLanguage,
  customLanguage,
  enabledSpecialsForRow,
  featuredPlacements,
  janeGold,
  menuTopRowInstance,
  pauseRefetching,
  shopAllProductCount,
  unpauseRefetching,
  userHasLinkedBank,
}: FeaturedTabProps) => {
  const { featureFlags, pinnedStore, whoami } = useRootLoaderData();
  const generatePath = useFullDestination();
  const authenticated = isJaneUser(whoami.user);
  const { skeletonLoader } = useSkeletonLoader();
  const useV3StoreSpecials = featureFlags[FLAGS.c3UseV3StoreSpecials];
  const storeSpecials = useMemo(() => {
    if (!isEmpty(enabledSpecialsForRow)) {
      return useV3StoreSpecials
        ? enabledSpecialsForRow
        : sortSpecials([...enabledSpecialsForRow]);
    }
    return null;
  }, [enabledSpecialsForRow, useV3StoreSpecials]);

  if (skeletonLoader) {
    return skeletonLoader;
  }

  const specialsRedesignMenu = featureFlags[FLAGS.c3SpecialsRedesignMenu];

  return (
    <>
      <Box className={styles['featuredContainer']}>
        {menuTopRowInstance && (
          <DMMenuRow
            pauseRefetching={pauseRefetching}
            unpauseRefetching={unpauseRefetching}
            brandSpecials={brandSpecials}
            menuTopRow={menuTopRowInstance}
            customLanguage={customLanguage}
            specials={storeSpecials}
            janeGold={janeGold}
          />
        )}
        {featuredPlacements.map((placement, index) => {
          if (
            typeof placement !== 'string' &&
            placement.placement === Placement.Specials &&
            storeSpecials
          ) {
            return (
              <SpecialsRow
                className="customRow"
                complianceLanguage={complianceLanguage}
                displayName={placement.title}
                key={`specials-placement-${index}`}
                reservationModeLabels={customLanguage.reservationModeLabels}
                specialsRedesignMenu={specialsRedesignMenu}
                storeSpecials={storeSpecials}
                {...(!specialsRedesignMenu && {
                  mode: 'ideal-width',
                })}
              />
            );
          } else if (
            typeof placement !== 'string' &&
            placement.placement === Placement.BrandSpecials &&
            janeGold.shouldShowGold
          ) {
            return (
              <JaneGoldRow
                authenticated={authenticated}
                brandSpecials={brandSpecials}
                className="janeGoldRow"
                isFeaturedTab={true}
                key={`brand-specials-placement-${index}`}
                state={pinnedStore.state}
                title={placement.title}
                userHasLinkedBank={userHasLinkedBank}
              />
            );
          } else if (typeof placement === 'string') {
            const instance =
              SmartSort.fromJSON<MaterializedMenuProduct>(placement);
            return (
              <MenuRow
                brandSpecials={brandSpecials}
                customLanguage={customLanguage}
                customRowId={instance.rowId ?? 0}
                customRowSlug={kebabCase(instance.title)}
                key={`menu-row-placement-${index}`}
                displayName={instance.title ?? null}
                specials={storeSpecials}
                stringifiedInstance={instance.toJSON()}
                janeGold={janeGold}
                placement={instance.placement}
              />
            );
          }

          return null;
        })}
      </Box>

      <Flex flexDirection="column" alignItems="center" gap={8} mt={24} px={16}>
        <Typography variant="h3-semibold" textAlign="center">
          Looking for something else?
        </Typography>

        {!!shopAllProductCount && (
          <Typography>
            Browse the full menu, {formatProductCount(shopAllProductCount)}
          </Typography>
        )}

        <Button label="Shop all products" to={generatePath('/shop-all')} />
      </Flex>
    </>
  );
};
